// League history and the record book (HISTORY.md): the remembered past
// plus this season, from the league's side rather than one manager's.

import type { AllTimeRow, HistoryView, RecordView, TeamRef } from '@fsp/domain';
import { longestRun, teamPeriods } from '@fsp/awards';
import { seasonFacts } from './awards';
import type { SimState } from './league';
import { leagueRef, meetingsOf, rivalryOf, standingsView, teamRef, titlesFor } from './views';

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function historyView(state: SimState, now: number): HistoryView {
  const inSeason = state.status === 'in-season';
  const teams = state.teams.filter((t) => t.ownerName);
  const refs = new Map<string, TeamRef>(teams.map((t) => [t.id, teamRef(state, t, now)]));
  const ref = (id: string | null | undefined) => (id ? (refs.get(id) ?? null) : null);
  const facts = inSeason ? seasonFacts(state, now) : null;
  const standings = inSeason ? standingsView(state, now).rows : [];

  // Seasons, newest first; the current one joins once the championship is final.
  const seasons: HistoryView['seasons'] = [];
  if (facts?.bracket.final) {
    const top = [...standings].sort((a, b) => b.pointsFor - a.pointsFor)[0];
    seasons.push({
      season: state.season,
      // The practice league's seasons are all its own league's.
      leagueId: null,
      final: true,
      champion: ref(facts.bracket.championTeamId),
      runnerUp: ref(facts.bracket.runnerUpTeamId),
      pointsTitle: top ? (refs.get(top.team.id) ?? null) : null,
      lastPlace: ref(facts.bracket.lastPlaceTeamId),
    });
  }
  for (const past of [...state.history].sort((a, b) => b.season - a.season)) {
    const byPoints = [...past.teams].sort((a, b) => b.pointsFor - a.pointsFor)[0];
    seasons.push({
      season: past.season,
      leagueId: null,
      final: true,
      champion: ref(past.teams.find((t) => t.champion)?.teamId),
      runnerUp: ref(past.teams.find((t) => t.runnerUp)?.teamId),
      pointsTitle: ref(byPoints?.teamId),
      lastPlace: ref(past.teams.find((t) => t.finish === t.of)?.teamId),
    });
  }

  // All-time rows: the past plus this season's regular-season record.
  const allTime: AllTimeRow[] = teams.map((team) => {
    const past = state.history
      .map((h) => h.teams.find((t) => t.teamId === team.id))
      .filter((t): t is NonNullable<typeof t> => t !== undefined);
    const row = standings.find((r) => r.team.id === team.id);
    const played = row ? row.wins + row.losses + row.ties > 0 : false;
    const wins = past.reduce((n, t) => n + t.wins, 0) + (row?.wins ?? 0);
    const losses = past.reduce((n, t) => n + t.losses, 0) + (row?.losses ?? 0);
    const ties = past.reduce((n, t) => n + t.ties, 0) + (row?.ties ?? 0);
    const games = wins + losses + ties;
    const inPlayoffsNow =
      !!facts &&
      facts.currentPeriod > facts.regularSeasonEnd &&
      facts.bracket.playoffTeamIds.includes(team.id);
    return {
      team: refs.get(team.id)!,
      rank: 0,
      seasons: past.length + (played ? 1 : 0),
      wins,
      losses,
      ties,
      pct: games ? Math.round(((wins + ties / 2) / games) * 1000) / 1000 : 0,
      titles: titlesFor(state, team.id),
      playoffTrips: past.filter((t) => t.playoffs).length + (inPlayoffsNow ? 1 : 0),
      pointsFor: round1(past.reduce((n, t) => n + t.pointsFor, 0) + (row?.pointsFor ?? 0)),
    };
  });
  allTime.sort((a, b) => b.pct - a.pct || b.titles - a.titles || b.pointsFor - a.pointsFor);
  allTime.forEach((r, i) => (r.rank = i + 1));

  // The record book: this season's facts against the remembered bests.
  const records: RecordView[] = [];
  type Best = { value: number; teamId: string; when: string } | null;
  const better = (a: Best, b: Best) => (!a ? b : !b ? a : b.value > a.value ? b : a);
  let highWeek: Best = null;
  let blowout: Best = null;
  let streak: Best = null;
  let seasonPoints: Best = null;
  for (const past of state.history)
    for (const t of past.teams) {
      highWeek = better(highWeek, {
        value: t.bestWeek,
        teamId: t.teamId,
        when: String(past.season),
      });
      blowout = better(blowout, {
        value: t.biggestMargin,
        teamId: t.teamId,
        when: String(past.season),
      });
      streak = better(streak, {
        value: t.longestStreak,
        teamId: t.teamId,
        when: String(past.season),
      });
      seasonPoints = better(seasonPoints, {
        value: t.pointsFor,
        teamId: t.teamId,
        when: String(past.season),
      });
    }
  if (facts) {
    for (const p of facts.periods) {
      if (!p.final) continue;
      for (const t of p.teams) {
        if (!t.played) continue;
        highWeek = better(highWeek, {
          value: t.score,
          teamId: t.teamId,
          when: `Week ${p.period}, ${state.season}`,
        });
        if (t.result === 'win' && t.opponentScore !== null)
          blowout = better(blowout, {
            value: round1(t.score - t.opponentScore),
            teamId: t.teamId,
            when: `Week ${p.period}, ${state.season}`,
          });
      }
    }
    for (const team of teams) {
      const run = longestRun(teamPeriods(facts, team.id), (t) => t.result === 'win');
      streak = better(streak, { value: run, teamId: team.id, when: String(state.season) });
      const row = standings.find((r) => r.team.id === team.id);
      if (row && row.pointsFor > 0)
        seasonPoints = better(seasonPoints, {
          value: row.pointsFor,
          teamId: team.id,
          when: facts.bracket.final ? String(state.season) : `${state.season}, so far`,
        });
    }
  }
  const push = (id: string, name: string, best: Best, unit: 'points' | 'count') => {
    if (!best || best.value <= 0) return;
    const holder = refs.get(best.teamId);
    if (!holder) return;
    const valueLabel = unit === 'points' ? best.value.toFixed(1) : String(Math.round(best.value));
    records.push({
      id,
      name,
      valueLabel,
      holder,
      when: best.when,
      line: `${holder.name} · ${best.when}`,
    });
  };
  push('high-week', 'Highest week', highWeek, 'points');
  push('blowout', 'Biggest blowout', blowout, 'points');
  push('streak', 'Longest win streak', streak, 'count');
  push('season-points', 'Most points in a season', seasonPoints, 'points');
  const mostTitles = [...allTime].sort((a, b) => b.titles - a.titles)[0];
  if (mostTitles && mostTitles.titles > 0)
    records.push({
      id: 'titles',
      name: 'Most titles',
      valueLabel: String(mostTitles.titles),
      holder: mostTitles.team,
      when: 'all time',
      line: `${mostTitles.team.name} · all time`,
    });

  // Head-to-head: the same series the matchup cards show.
  const headToHead: HistoryView['headToHead'] = teams.map((team) => ({
    team: refs.get(team.id)!,
    vs: teams
      .filter((o) => o.id !== team.id)
      .map((o) => {
        const r = rivalryOf(state, team, o, now);
        return {
          opponent: refs.get(o.id)!,
          wins: r?.wins ?? 0,
          losses: r?.losses ?? 0,
          ties: r?.ties ?? 0,
          // THE TOUR'S MEETINGS GAME BY GAME (B-418, B-524): this season's
          // from its schedule, and each past season's record from its history.
          games: meetingsOf(state, team.id, o.id, now),
          seasons: [...state.history]
            .sort((a, b) => b.season - a.season)
            .flatMap((season) => {
              const rec = season.teams.find((t) => t.teamId === team.id)?.vs?.[o.id];
              return rec === undefined || rec.wins + rec.losses + rec.ties === 0
                ? []
                : [{ season: season.season, wins: rec.wins, losses: rec.losses, ties: rec.ties }];
            }),
        };
      }),
  }));

  return { league: leagueRef(state), seasons, allTime, records, headToHead, updatedAt: iso(now) };
}
